import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Skeleton,
} from '@mui/material'
import PageHelpButton from '../../components/PageHelpButton'
import type { PortalSectionProps } from '../../components/portal/portalSection'
import { tasksApi, Task } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket, WS_RECONNECTED } from '../../hooks/useWebSocket'
import { applyTaskEvent, withoutIds } from './portalTasksLive'

// Returning to the tab fires both visibilitychange and focus; one refetch covers the pair.
const RETURN_REFETCH_DEBOUNCE_MS = 1000
// A live message can be lost without trace (MWA-390), and a tab that stays focused never hits
// the return refetch; this bounds how stale a visible list can get.
const RECONCILE_REFETCH_MS = 60_000

const priorityColors: Record<string, 'error' | 'warning' | 'success'> = {
  high: 'error',
  medium: 'warning',
  low: 'success',
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function PortalTasks({ embedded }: PortalSectionProps = {}) {
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [completing, setCompleting] = useState<string | null>(null)

  const mounted = useRef(true)
  const loadInFlight = useRef(false)
  const refetchQueued = useRef(false)
  const lastReturnRefetchAt = useRef(0)
  // Live messages that arrive while a fetch is out are replayed over its result, which
  // may have been read from the server before the change they describe.
  const bufferedEvents = useRef<{ action: string; data: unknown }[]>([])
  // A fetch started before the client's own Complete finished must not bring that row back.
  const clock = useRef(0)
  const completedAt = useRef(new Map<string, number>())

  const load = useCallback(async (initial: boolean) => {
    if (loadInFlight.current) {
      refetchQueued.current = true
      return
    }
    loadInFlight.current = true
    bufferedEvents.current = []
    const startedAt = ++clock.current
    try {
      const taskData = await tasksApi.list()
      if (!mounted.current) return
      const replayed = bufferedEvents.current.reduce(
        (list, e) => applyTaskEvent(list, e.action, e.data),
        taskData
      )
      const completedSince = new Set<string>()
      completedAt.current.forEach((at, id) => {
        if (at > startedAt) completedSince.add(id)
      })
      setTasks(withoutIds(replayed, completedSince))
    } catch (err) {
      console.error('Failed to load tasks:', err)
      // A failed refetch keeps what is on screen; only the first load has nothing to show.
      if (initial && mounted.current) setError('Failed to load tasks')
    } finally {
      loadInFlight.current = false
      bufferedEvents.current = []
      if (initial && mounted.current) setLoading(false)
      if (refetchQueued.current && mounted.current) {
        refetchQueued.current = false
        void load(false)
      }
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    void load(true)
    return () => {
      mounted.current = false
    }
  }, [load])

  useEffect(() => {
    const refetchOnReturn = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastReturnRefetchAt.current < RETURN_REFETCH_DEBOUNCE_MS) return
      lastReturnRefetchAt.current = now
      void load(false)
    }
    document.addEventListener('visibilitychange', refetchOnReturn)
    window.addEventListener('focus', refetchOnReturn)
    return () => {
      document.removeEventListener('visibilitychange', refetchOnReturn)
      window.removeEventListener('focus', refetchOnReturn)
    }
  }, [load])

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || loadInFlight.current) return
      void load(false)
    }, RECONCILE_REFETCH_MS)
    return () => window.clearInterval(id)
  }, [load])

  const wsHandlers = useMemo(() => {
    const onTaskEvent = (action: string) => (message: { data: unknown }) => {
      if (loadInFlight.current) bufferedEvents.current.push({ action, data: message.data })
      setTasks((prev) => applyTaskEvent(prev, action, message.data))
    }
    return {
      task_created: onTaskEvent('task_created'),
      task_updated: onTaskEvent('task_updated'),
      task_deleted: onTaskEvent('task_deleted'),
      [WS_RECONNECTED]: () => {
        void load(false)
      },
    }
  }, [load])

  useWebSocket(wsHandlers)

  const handleComplete = async (taskId: string) => {
    setCompleting(taskId)
    try {
      await tasksApi.complete(taskId)
      completedAt.current.set(taskId, ++clock.current)
      setTasks((prev) => prev.filter((t) => t.id !== taskId))
      showSuccess('Task completed!')
    } catch (err) {
      console.error('Failed to complete task:', err)
      showError('Failed to complete task')
    } finally {
      setCompleting(null)
    }
  }

  // The page's own shape rather than a centred spinner, which threw the
  // heading away and rebuilt it on every load.
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={26} />
        <Skeleton variant="text" width={300} height={20} sx={{ mb: 3 }} />
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: '12px' }} />
        </Stack>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  const pendingTasks = tasks.filter((t) => t.status !== 'completed')
  const completedTasks = tasks.filter((t) => t.status === 'completed')

  return (
    <Box>
      {/* Suppressed when this renders as a tab of My Profile, which supplies
          the page heading. */}
      {!embedded && (
        <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          My Tasks
        </Typography>
        <PageHelpButton articleId="page-portal-tasks" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tasks assigned to you by your career consultant.
      </Typography>
        </>
      )}

      {pendingTasks.length === 0 && completedTasks.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No tasks yet. Your strategist will assign tasks as your engagement progresses.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {pendingTasks.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Pending Tasks ({pendingTasks.length})
                </Typography>
                <List>
                  {pendingTasks.map((task) => (
                    <ListItem
                      key={task.id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: 'action.hover',
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={false}
                          onChange={() => handleComplete(task.id)}
                          disabled={completing === task.id}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{task.title}</Typography>
                            <Chip
                              label={task.priority}
                              size="small"
                              color={priorityColors[task.priority] || 'default'}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {task.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {task.description}
                              </Typography>
                            )}
                            {task.dueDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Due: {formatDate(task.dueDate)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {completing === task.id && <CircularProgress size={20} />}
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {completedTasks.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                  Completed ({completedTasks.length})
                </Typography>
                <List>
                  {completedTasks.map((task) => (
                    <ListItem
                      key={task.id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        opacity: 0.7,
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox edge="start" checked disabled />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                          >
                            {task.title}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  )
}
